import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { getCollegeInfo, generateReceiptFooter, fetchLogoAsBase64, fetchReceiptTerms } from './receiptHeaderUtils';
import { ensureRupeeFont, formatINR } from './jspdfFonts';
import { formatDate } from '../utils/dateTimeUtils';
applyPlugin(jsPDF);

// Common header generator for all fee receipts with logo support
const generateHeader = async (doc, collegeInfo, title = 'FEE PAYMENT RECEIPT', logoBase64 = null) => {
  const resolvedFont = await ensureRupeeFont(doc);
  const pageWidth = doc.internal.pageSize.width;
  const margin = 20;
  let yPos = 15;

  const collegeName = collegeInfo?.college_name || collegeInfo?.name || 'Institution';
  const collegeAddress = collegeInfo?.address || '';
  const collegeCity = collegeInfo?.city || '';
  const collegeState = collegeInfo?.state || '';
  const collegePincode = collegeInfo?.pincode || collegeInfo?.pin_code || '';
  const collegePhone = collegeInfo?.phone || '';
  const collegeEmail = collegeInfo?.email || '';
  const collegeLogo = logoBase64 || collegeInfo?.logo_base64 || collegeInfo?.logo_url || collegeInfo?.logo || '';

  // Add logo if available
  let logoAdded = false;
  if (collegeLogo) {
    try {
      const logoWidth = 20;
      const logoHeight = 20;
      const logoX = margin;
      
      if (collegeLogo.startsWith('data:image')) {
        doc.addImage(collegeLogo, 'PNG', logoX, yPos - 5, logoWidth, logoHeight);
        logoAdded = true;
      }
    } catch (e) {
      console.log('Could not add logo:', e);
    }
  }

  // Adjust text position based on logo
  const textStartX = logoAdded ? margin + 25 : pageWidth / 2;
  // Calculate max width for text (considering logo space)
  const maxTextWidth = logoAdded ? (pageWidth - margin - textStartX - 5) : (pageWidth - 2 * margin);

  // College Name
  doc.setFontSize(16);
  doc.setFont(resolvedFont, 'bold');
  doc.setTextColor(0, 51, 102);
  
  if (logoAdded) {
    doc.text(collegeName.toUpperCase(), textStartX, yPos + 2);
  } else {
    doc.text(collegeName.toUpperCase(), pageWidth / 2, yPos, { align: 'center' });
  }
  yPos += 6;

  // Full Address with text wrapping for long addresses
  doc.setFontSize(9);
  doc.setFont(resolvedFont, 'normal');
  doc.setTextColor(60, 60, 60);

  if (collegeAddress) {
    const wrappedAddress = doc.splitTextToSize(collegeAddress, maxTextWidth);
    wrappedAddress.forEach((line) => {
      if (logoAdded) {
        doc.text(line, textStartX, yPos);
      } else {
        doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      }
      yPos += 4;
    });
  }

  const cityStatePincode = [collegeCity, collegeState, collegePincode].filter(Boolean).join(', ');
  if (cityStatePincode) {
    const wrappedCity = doc.splitTextToSize(cityStatePincode, maxTextWidth);
    wrappedCity.forEach((line) => {
      if (logoAdded) {
        doc.text(line, textStartX, yPos);
      } else {
        doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      }
      yPos += 4;
    });
  }

  // Phone and Email on same line with wrapping
  const contactParts = [];
  if (collegePhone) contactParts.push(`Phone: ${collegePhone}`);
  if (collegeEmail) contactParts.push(`Email: ${collegeEmail}`);
  
  if (contactParts.length > 0) {
    doc.setFontSize(8);
    const contactText = contactParts.join('   |   ');
    const wrappedContact = doc.splitTextToSize(contactText, maxTextWidth);
    wrappedContact.forEach((line) => {
      if (logoAdded) {
        doc.text(line, textStartX, yPos);
      } else {
        doc.text(line, pageWidth / 2, yPos, { align: 'center' });
      }
      yPos += 4;
    });
    yPos += 1;
  }

  // Ensure yPos is below logo
  if (logoAdded) {
    yPos = Math.max(yPos, 35);
  }

  // Decorative double line
  doc.setDrawColor(0, 51, 102);
  doc.setLineWidth(1);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 2;
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 8;

  // Receipt Title
  doc.setFontSize(14);
  doc.setFont(resolvedFont, 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(title, pageWidth / 2, yPos, { align: 'center' });
  yPos += 3;

  const titleWidth = doc.getTextWidth(title);
  doc.setLineWidth(0.5);
  doc.line((pageWidth - titleWidth) / 2 - 5, yPos, (pageWidth + titleWidth) / 2 + 5, yPos);
  yPos += 8;

  doc.setTextColor(0, 0, 0);
  return yPos;
};

export const generateFeePaymentReceipt = async (paymentData, collegeInfo = null) => {
  const doc = new jsPDF();
  const resolvedFont = await ensureRupeeFont(doc);
  const CURRENCY = resolvedFont === 'FreeSans' ? '\u20B9' : 'Rs.';
  const pageWidth = doc.internal.pageSize.width;

  // Get college info
  if (!collegeInfo) {
    collegeInfo = getCollegeInfo() || {};
  }

  // Fetch logo as base64 if URL is provided
  let logoBase64 = collegeInfo.logo_base64 || null;
  if (!logoBase64 && (collegeInfo.logo_url || collegeInfo.logo)) {
    logoBase64 = await fetchLogoAsBase64(collegeInfo.logo_url || collegeInfo.logo);
  }

  // Generate Header with logo
  let yPos = await generateHeader(doc, collegeInfo, 'FEE PAYMENT RECEIPT', logoBase64);

  // Receipt Info
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  doc.text(`Receipt No: ${paymentData.receipt_no || 'N/A'}`, 20, yPos);
  doc.text(`Date: ${formatDate(paymentData.payment_date || new Date())}`, pageWidth - 60, yPos);
  yPos += 10;

  // Student Information
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'bold');
  doc.setFillColor(240, 240, 240);
  doc.rect(20, yPos - 5, pageWidth - 40, 8, 'F');
  doc.text('STUDENT INFORMATION', 22, yPos);
  yPos += 8;
  
  doc.setFontSize(10);
  doc.setFont(resolvedFont, 'normal');
  doc.text(`Name: ${paymentData.student_name || 'N/A'}`, 20, yPos);
  doc.text(`Admission No: ${paymentData.admission_no || paymentData.student_id || 'N/A'}`, pageWidth / 2, yPos);
  yPos += 6;
  doc.text(`Course: ${paymentData.course_name || 'N/A'}`, 20, yPos);
  doc.text(`Session: ${paymentData.session || 'N/A'}`, pageWidth / 2, yPos);
  yPos += 10;

  // Payment Details Box
  doc.setFillColor(245, 245, 245);
  doc.rect(20, yPos, 170, 35, 'F');
  
  doc.setFont(resolvedFont, 'bold');
  doc.setFontSize(11);
  doc.text('Payment Details', 25, yPos + 8);
  
  doc.setFont(resolvedFont, 'normal');
  doc.setFontSize(10);
  doc.text(`Amount Paid:`, 25, yPos + 16);
  doc.setFont(resolvedFont, 'bold');
  doc.setFontSize(14);
  doc.text(`${CURRENCY} ${formatINR(parseFloat(paymentData.amount_paid || 0))}`, 25, yPos + 24);
  
  doc.setFont(resolvedFont, 'normal');
  doc.setFontSize(10);
  doc.text(`Payment For: ${paymentData.payment_for || 'Course Fee'}`, 25, yPos + 32);

  yPos += 45;

  // Fee Summary
  doc.setFontSize(11);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Fee Summary', 20, yPos);
  yPos += 5;

  const summaryData = [
    ['Total Course Fee', `${CURRENCY} ${formatINR(parseFloat(paymentData.total_fee || 0))}`],
    ['Discount', `${CURRENCY} ${formatINR(parseFloat(paymentData.discount_amount || 0))}`],
    ['Scholarship', `${CURRENCY} ${formatINR(parseFloat(paymentData.scholarship_amount || 0))}`],
    ['Previously Paid', `${CURRENCY} ${formatINR(parseFloat(paymentData.previously_paid || 0))}`],
    ['Current Payment', `${CURRENCY} ${formatINR(parseFloat(paymentData.amount_paid || 0))}`],
    ['Total Paid (After This Payment)', `${CURRENCY} ${formatINR(parseFloat(paymentData.total_paid_after || 0))}`],
    ['Outstanding Balance', `${CURRENCY} ${formatINR(parseFloat(paymentData.outstanding_balance || 0))}`]
  ];

  doc.autoTable({
    startY: yPos,
    head: [['Description', 'Amount']],
    body: summaryData,
    theme: 'striped',
    headStyles: { fillColor: [0, 51, 102], textColor: 255 },
    styles: { fontSize: 9 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 70, halign: 'right', fontStyle: 'bold' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 10;

  // Payment Breakdown
  if (paymentData.payment_modes && paymentData.payment_modes.length > 0) {
    doc.setFontSize(11);
    doc.setFont(resolvedFont, 'bold');
    doc.text('Payment Breakdown', 20, yPos);
    yPos += 5;

    const paymentTable = paymentData.payment_modes.map(pm => {
      const transactionInfo = pm.transaction_number ? ` (Ref: ${pm.transaction_number})` : '';
      return [
        String(pm.mode || 'N/A') + transactionInfo,
        `${CURRENCY} ${formatINR((parseFloat(pm.amount) || 0))}`
      ];
    });

    doc.autoTable({
      startY: yPos,
      head: [['Payment Mode', 'Amount']],
      body: paymentTable,
      theme: 'plain',
      headStyles: { fillColor: [230, 230, 230], textColor: 0, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 3 , font: resolvedFont },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 70, halign: 'right' }
      }
    });

    yPos = doc.lastAutoTable.finalY + 8;
  }

  // Remarks
  if (paymentData.remarks) {
    doc.setFontSize(10);
    doc.setFont(resolvedFont, 'bold');
    doc.text('Remarks:', 20, yPos);
    doc.setFont(resolvedFont, 'normal');
    doc.text(paymentData.remarks, 50, yPos);
    yPos += 10;
  }

  // Footer with tenant-specific Installment terms
  const terms = await fetchReceiptTerms('installment');
  generateReceiptFooter(doc, collegeInfo, terms);

  // Save the PDF
  const fileName = `FeePayment_${paymentData.receipt_no || 'Receipt'}_${Date.now()}.pdf`;
  doc.save(fileName);
  return doc;
};

export default generateFeePaymentReceipt;
